import { Appointment } from '@2meters/types'
import { TicketTabId, TicketTabType } from '../types'
import { useTranslation } from 'utils/useTranslation'

interface TabsProps {
  app: Appointment | undefined
  currentTab: TicketTabType | undefined
  onChange: (tab: TicketTabType) => void
  showChat?: boolean
}

export const Tabs = ({ app, currentTab, onChange, showChat }: TabsProps) => {
  const intl = useTranslation()
  const classNames = (...classes) => classes.filter(Boolean).join(' ')

  const isEmpty = obj => {
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        return false
      }
    }
    return true
  }

  const makeTab = (id: TicketTabId, labelId: string): TicketTabType => ({
    id,
    name: intl({ id: labelId }),
    current: false,
  })

  const hasUnreadChat = (app?.unreadCounterMessages ?? 0) > 0

  const tabs: TicketTabType[] = [
    makeTab('details', 'details'),

    ...(showChat ? [makeTab('chat', 'ticket.tab.chat')] : []),

    ...(!isEmpty(app?.formData?.data) ? [makeTab('info', 'info')] : []),

    ...(!isEmpty(app?.contactData) ? [makeTab('personal', 'ticket.tab.myData')] : []),

    ...(app?.order ? [makeTab('products', 'products')] : []),
  ]

  return (
    <div>
      <div className='block'>
        <div className='border-b border-gray-200'>
          <div className='relative'>
            <nav
              className='-mb-px flex overflow-x-auto'
              aria-label='Tabs'
              style={{ scrollbarWidth: 'none' }}
            >
              {tabs.map(tab => (
                <a
                  href='#'
                  key={tab.id}
                  onClick={e => {
                    e.preventDefault()
                    onChange(tab)
                  }}
                  className={classNames(
                    tab.id === currentTab?.id
                      ? 'border-indigo-500 text-gray-600'
                      : 'border-transparent text-gray-600 opacity-30 hover:border-gray-300 hover:text-gray-700',
                    'whitespace-nowrap border-b-4 pt-4 pb-2 px-3 text-sm font-medium first:pl-4 last:pr-4'
                  )}
                  aria-current={tab.id === currentTab?.id ? 'page' : undefined}
                >
                  <span className='flex items-center gap-1.5'>
                    {tab.name}
                    {tab.id === 'chat' && hasUnreadChat && (
                      <span className='w-2 h-2 rounded-full bg-red-500 flex-shrink-0' />
                    )}
                  </span>
                </a>
              ))}
            </nav>
            {/* Scroll hint gradient — visible only when tabs overflow on mobile */}
            <div
              className='pointer-events-none absolute right-0 top-0 h-full w-8 bg-gradient-to-l from-white to-transparent'
              aria-hidden='true'
            />
          </div>
        </div>
      </div>
    </div>
  )
}
